import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '../../webcommon/grid/Grid';
import { ReactComponent as AddCircle } from '../../webcommon/common/Icons/Add-Circle.svg';
import Modal from '../../webcommon/common/modal/Modal';
import { noop } from 'lodash';
import GridHeader from '../../common/gridheader/GridHeader';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import gridHeaderStyles from './GridHeaderOverride.module.scss';
import gridStyles from './GridOverride.module.scss';
import SimpleGridHeaderButton from '../../common/gridheader/simplegridheader/SimpleGridHeaderButton';
import AddNewRow from './ModalComponents/AddNewRow';
import ConfirmDelete from './ModalComponents/ConfirmDelete';

const CustomGridWithModal = (props) => {
    const {
        fetchDropdownOptions,
        rows,
        createAction,
        editMapping
    } = props;

    const [newMappingModalOpen, setNewMappingModalOpen] = useState(false);

    const [optionOne, setOptionOne] = useState('');
    const [optionTwo, setOptionTwo] = useState('');

    const openNewMappingModal = useCallback(() => {
        setOptionOne('');
        setOptionTwo('');
        fetchDropdownOptions();
        setNewMappingModalOpen(true);
    }, [setNewMappingModalOpen, fetchDropdownOptions]);

    const closeNewMappingModal = useCallback(
        () => setNewMappingModalOpen(false), [setNewMappingModalOpen]
    );

    const [editId, setEditId] = useState('');

    const setOne = useCallback((val) => { setOptionOne(val); }, [setOptionOne]);
    const setTwo = useCallback(val => setOptionTwo(val), [setOptionTwo]);

    const [editMappingModalOpen, setEditMappingModalOpen] = useState(false);
    const [deleteMappingModalOpen, setDeleteMappingModalOpen] = useState(false);

    const closeEditMappingModal = useCallback(
        () => setEditMappingModalOpen(false), [setEditMappingModalOpen]
    );


    const [idOfRowToDelete, setIdOfRowToDelete] = useState('');
    const openDeleteMappingModal = useCallback(
        (id) => {
            setIdOfRowToDelete(id);
            setDeleteMappingModalOpen(true);
        }, [setDeleteMappingModalOpen, setIdOfRowToDelete]
    );

    const closeDeletedMappingModal = useCallback(
        () => {
            setIdOfRowToDelete(null);
            setDeleteMappingModalOpen(false);
        },
        [setDeleteMappingModalOpen, setIdOfRowToDelete]
    );

    const openEditMappingModal = useCallback((id) => {
        fetchDropdownOptions();
        const row = rows.find(row => row.id === id);
        if (row) {
            setOptionOne(row.field);
            setOptionTwo(row.mappedField);
            setEditId(id);
            setEditMappingModalOpen(true);
        }
    }, [rows, fetchDropdownOptions]);

    const createNewMapping = useCallback(() => {
        createAction(optionOne, optionTwo);
    }, [createAction, optionOne, optionTwo]);

    const editMappingFunc = useCallback(() => {
        editMapping(editId, optionOne, optionTwo);
    }, [editMapping, editId, optionOne, optionTwo]);

    const openDeleteRowAndSetId = (id) => {
        openDeleteMappingModal(id);
    };

    return (
        <div className={ props.styles.customGrid }>
            <GridHeader
                title={ props.title }
                styles={ gridHeaderStyles }
            >
                {!props.hideCreateModal && (
                    <>
                        <SimpleGridHeaderButton
                            onClick={ openNewMappingModal }
                            text="New Row"
                        ><AddCircle />
                        </SimpleGridHeaderButton>
                        <div className={ props.styles.separator }>&nbsp;</div>
                    </>
                )
                }
                <ExportAsCsvButton
                    csvData={ props.rows }
                    csvFilename={ props.csvFilename }
                    csvMapping={ props.csvMapping }
                />

            </GridHeader>
            <Grid
                defaultColumnSpacing={ 2 }
                defaultSorted={ props.defaultSorted }
                deleteRow={ openDeleteRowAndSetId }
                editRow={ openEditMappingModal }
                filterable={ props.filterable }
                onSortedChange={ props.onSortedChange }
                loading={ props.loading }
                styles={ gridStyles }
                columns={ props.columns }
                rows={ props.rows }
                pagination={ props.pagination }
            />
            <Modal
                isOpen={ newMappingModalOpen }
                closeModal={ closeNewMappingModal }
            >
                <AddNewRow
                    closeModal={ closeNewMappingModal }
                    createAction={ createNewMapping }
                    dropdownOptions={ props.dropdownOptions }
                    modalMessage={ props.createMappingMessage }
                    optionOneKey={ props.optionOneKey }
                    optionOneValue={ optionOne }
                    optionTwoKey={ props.optionTwoKey }
                    optionTwoValue={ optionTwo }
                    secondOptionDropdown={ props.secondOptionDropdown }
                    setOptionOne={ setOne }
                    setOptionTwo={ setTwo }
                    onlyOneInputOption={ props.onlyOneInputOption }
                />
            </Modal>
            <Modal
                isOpen={ editMappingModalOpen }
                closeModal={ closeEditMappingModal }
            >
                <AddNewRow
                    closeModal={ closeEditMappingModal }
                    createAction={ editMappingFunc }
                    dropdownOptions={ props.dropdownOptions }
                    isEdit
                    modalMessage={ props.editMappingMessage }
                    optionOneKey={ props.optionOneKey }
                    optionOneValue={ optionOne }
                    optionTwoKey={ props.optionTwoKey }
                    optionTwoValue={ optionTwo }
                    secondOptionDropdown={ props.secondOptionDropdown }
                    setOptionOne={ setOne }
                    setOptionTwo={ setTwo }
                    firstOptionReadOnly={ props.firstOptionReadOnly }
                />
            </Modal>
            <Modal
                isOpen={ deleteMappingModalOpen }
                closeModal={ closeDeletedMappingModal }
            >
                <ConfirmDelete
                    closeModal={ closeDeletedMappingModal }
                    confirmDeleteMapping={ props.deleteRow }
                    idOfRowToDelete={ idOfRowToDelete }
                    deleteMessage={ props.deleteMessage }
                />

            </Modal>
        </div>
    );
};

CustomGridWithModal.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string
    })),
    csvFilename: PropTypes.string,
    csvMapping: PropTypes.func,
    createAction: PropTypes.func,
    createMappingMessage: PropTypes.string,
    deleteRow: PropTypes.func,
    defaultSorted: PropTypes.arrayOf(
        PropTypes.shape({
            gridId: PropTypes.string,
            gridSort: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool
            }))
        })
    ),
    dropdownOptions: PropTypes.arrayOf(PropTypes.string),
    editMapping: PropTypes.func,
    editMappingMessage: PropTypes.string,
    fetchDropdownOptions: PropTypes.func,
    filterable: PropTypes.bool,
    loading: PropTypes.bool,
    onSortedChange: PropTypes.func,
    optionOneKey: PropTypes.string,
    optionTwoKey: PropTypes.string,
    pagination: PropTypes.bool,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    secondOptionDropdown: PropTypes.bool,
    styles: PropTypes.objectOf(PropTypes.string),
    title: PropTypes.string,
    onlyOneInputOption: PropTypes.bool,
    deleteMessage: PropTypes.string,
    hideCreateModal: PropTypes.bool,
    firstOptionReadOnly: PropTypes.bool
};

CustomGridWithModal.defaultProps = {
    columns: [],
    csvFilename: 'file.csv',
    csvMapping: row => row,
    createAction: noop,
    createMappingMessage: '',
    defaultSorted: [],
    deleteRow: noop,
    dropdownOptions: [],
    editMapping: noop,
    editMappingMessage: '',
    fetchDropdownOptions: noop,
    filterable: false,
    loading: false,
    onSortedChange: noop,
    optionOneKey: '',
    optionTwoKey: '',
    pagination: false,
    rows: [],
    secondOptionDropdown: false,
    styles: {},
    title: '',
    onlyOneInputOption: false,
    deleteMessage: undefined,
    hideCreateModal: false,
    firstOptionReadOnly: false
};

Modal.setAppElement('body');

export default CustomGridWithModal;
